/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import { loc } from "../../../../core/utils"
import {
  DATA_INIZIO_LAVARO_NO_VALIDATE_PROFESSIONS,
  DATE_FORMAT,
  DATORE_CITTA_PROVINCIA_NO_VALIDATE_PROFESSIONS,
  IDENTITY_DOCUMENT_TYPE_PATENTE,
  REDITTO_MENSILE_IN_EURO_NO_VALIDATE_PROFESSIONS,
  contractTypeEnum
} from "../../constants/ClientPageConstants"
import genericSchema from "../ClientPageValidator"
import CodiceFiscale from "codice-fiscale-js"
import moment from "moment"
import * as yup from "yup"

/* regex */
const employmentDateRegex = /^[0-9]{2}\/[0-9]{4}$/
const fixePhoneNumberRegex = /^0\d{8,11}$/
const postalCodeRegex = /^\d{5}$/
const cnNumberWithConditionContriesRegex = /^(A|a)(?:[A-Ba-b])[0-9]{6}$/
const cnConditionDateOfIssue = /^(A|a)(?:[U-Vu-v])[0-9]{7}$/
const cnNumberRegex = /^(A|a)(?:(?![PQWpqw])[O-Zo-z])[0-9]{7}$/
const ceNumberRegex = /^((^(AA|aa|aA|Aa)([0-9]{7}))|(^([0-9]{7})(AA|aa|aA|Aa))|((CA|ca|Ca|cA)[0-9]{5}[A-Za-z]{2}$))$/
const paNumberRegex = /^[0-9A-z]*[0-9]+[0-9A-z]*$/
const phoneNumberRegex = /(^\+393\d{8,9})|(^393\d{8,9})$/
const emailRegex = /^[a-zA-Z0-9_\-.]+@[a-zA-Z0-9_\-.]+\.[a-zA-Z0-9_\-.]+$/
const peRegexDefault = /^([A-Z]{2}[0-9]{7}[A-Z])$|^((U|u)[0-9a-zA-Z]{8}[a-zA-Z])$/
const retroPatenteRegex = /^[A-Fa-f]{1}[0-9]{7}$/
const retroPatenteElectronicaRegex = /^A[A-Na-n]{1}[0-9]{7}$/
const retroPatenteElectronicaBolzanoRegex = /^BZ[0-9]{7}$/
const retroPatenteElectronicaBolzanoSecondRegex = /^BZP[0-9]{6}$/
const netMonthlyIncomeRegex = /^[1-9]{1}[0-9]{1,8}$/
const vatNumberRegex = /^[a-zA-Z0-9]{11}$/

/* error messages */
const requiredMessage = () => loc`Required`
const employmentDateIvalidMessage = () => loc`Not a valid MM/YYYY date`
const futureEmploymentDateMessage = () => loc`Employment date cannot be in future`
const pastEndContractDate = () => loc("validator_endContractDateInFuture")
const employmentDateBeforeBirthDateMessage = () => loc`Employment date cannot be before birth date`
const wrongDiffMessage = () => loc`difference between employment date and birth date must be at least 16 years`
const taxNumberInvalidMessage = () => loc`Not a valid tax number`
const maximumCharactersMessage = (maximumValue) => maximumValue + " " + loc`maximum characters`
const fixePhoneInvalidMessage = () => loc`Invalid format fixe phone`
const postalCodeMessage = () => loc`Invalid postal code`
const identityNumberInvalid = () => loc`Identity document / driving license number: invalid`
const patenteElectroniquaInvalid = () =>
  loc`Patente elettronica, if I follow the rule which starts with U and I do not choose the correct city Roma, when I confirm the page, put the message`
const documentDateOfIssueInvalid = () => loc`Identity document issue date: Invalid date / DD / MM / YYYY format`
const documentNotValidMessage = () => loc`Not valid document`
const phoneNumberInvalidMessage = () => loc`Invalid phone number`
const emailNotValidMessage = () => loc`Email invalid`
const invalideDateErrorMessage = () => loc`Not a valid date`
const birthDateMinMessage = () => loc`Minimum age must be > 18 years old`
const birthDateMaxessage = () => loc`Maximum age must be < 110 years old`
const dateYYYYFormatValidationMessage = () => loc`Not a valid YYYY date`
const futureResidenceDateMessage = () => loc`This date cannot be in future`
const residenceDateBeforeBirthDateMessage = () => loc`Residence date cannot be before birth date`
const yearSeniorityBeforeBirthDateMessage = () => loc`Year seniority bank cannot be before birth date`
const invalidBirthDateMessage = () => loc`birthDate is not valid`
const invalidDateResidencepermit = () => loc`Invalid date in past`
const invalidVatNumber = () => loc`Invalid VAT Number`
const invalidNetMonthlyIncomeFormat = () => loc("validator_invalidNetMounthlyIncomeFormat")

let schema = yup.object().shape({
  identity: yup.object().shape({
    name: yup.string().nullable().required(requiredMessage).max(25, maximumCharactersMessage(25)),
    firstName: yup.string().nullable().max(25, maximumCharactersMessage(25)).required(requiredMessage),
    taxNumber: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "taxNumber",
        message: "",
        test(value) {
          try {
            let codiceFiscale = new CodiceFiscale(value)
            let birthDate = this.options.context.identity.birthDate.split("/")
            var cf = new CodiceFiscale({
              // first name ( prenom ) et name ( nom de famille)
              name: this.options.context.identity.firstName,
              surname: this.options.context.identity.name,
              gender: getTitleFromEnum(this.options.context.identity.title),
              day: birthDate[0],
              month: birthDate[1],
              year: birthDate[2],
              //unused value for checking but mandatory field for instance
              birthplace: "Napoli"
            })
            if (codiceFiscale.code.substring(0, 11) !== cf.code.substring(0, 11)) {
              return this.createError({
                message: taxNumberInvalidMessage,
                path: "identity.taxNumber"
              })
            }
          } catch (error) {
            return this.createError({
              message: taxNumberInvalidMessage,
              path: "identity.taxNumber"
            })
          }
        }
      }),
    birthDate: yup
      .string()
      .nullable()
      .test({
        name: "ageTest",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "identity.birthDate"
            })
          }
          const birthDate = moment(value, DATE_FORMAT.identity.birthDate.IT)
          if (!birthDate.isValid() || birthDate.year() < 1000 || birthDate.year() > 9999) {
            return this.createError({
              message: invalideDateErrorMessage,
              path: "identity.birthDate"
            })
          }
          let currentDate = moment()
          if (!(currentDate.diff(birthDate, "years") >= 18)) {
            return this.createError({
              message: birthDateMinMessage,
              path: "identity.birthDate"
            })
          }
          if (!(currentDate.diff(birthDate, "years") < 110)) {
            return this.createError({
              message: birthDateMaxessage,
              path: "identity.birthDate"
            })
          }
        }
      })
  }),
  coordinates: yup.object().shape({
    postalCode: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "addressAddressTest",
        message: "",
        test(value) {
          if (!value?.match(postalCodeRegex)) {
            return this.createError({
              message: postalCodeMessage,
              path: "coordinates.postalCode"
            })
          }
        }
      }),
    province: yup.string().nullable().required(requiredMessage),
    phoneNumber: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "phoneNumber",
        message: "",
        test(value) {
          if (!value?.match(phoneNumberRegex)) {
            return this.createError({
              message: phoneNumberInvalidMessage,
              path: "coordinates.phoneNumber"
            })
          }
        }
      }),
    email: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "email",
        message: "",
        test(value) {
          if (!value?.match(emailRegex)) {
            return this.createError({
              message: emailNotValidMessage,
              path: "coordinates.email"
            })
          }
        }
      }),
    fixePhoneNumber: yup
      .string()
      .nullable()
      .test({
        name: "fixePhoneNumber",
        message: "",
        test(value) {
          if (value != null && !value?.match(fixePhoneNumberRegex)) {
            return this.createError({
              message: fixePhoneInvalidMessage,
              path: "coordinates.fixePhoneNumber"
            })
          }
        }
      })
  }),
  identityDocuments: yup.object().shape({
    citizenshipSimplified: yup.string().nullable().required(requiredMessage),
    citizenshipExtended: yup
      .string()
      .nullable()
      .test({
        name: "citizenshipExtended",
        message: "",
        test(value) {
          let citizenshipSimplified = this.options.context.identityDocuments.citizenshipSimplified
          if (citizenshipSimplified && ["2", "3"].includes(citizenshipSimplified.value) && !value) {
            return this.createError({
              message: requiredMessage,
              path: "identityDocuments.citizenshipExtended"
            })
          }
          return true
        }
      }),
    identityDocumentType: yup.string().nullable().required(requiredMessage),
    identityDocumentsNumber: yup
      .string()
      .nullable()
      .test({
        name: "identityDocumentsNumber verification",
        message: "",
        test(value) {
          const { documentPlaceOfIssue, identityDocumentType, identityDocumentsNumber } =
            this.options.context.identityDocuments
          const documentDateOfIssue = moment(
            this.options.context.identityDocuments.documentDateOfIssue,
            DATE_FORMAT.identityDocuments.documentDateOfIssue.IT
          )
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "identityDocuments.identityDocumentsNumber"
            })
          }
          if (!identityDocumentType?.value) {
            return this.createError({
              message: requiredMessage,
              path: "identityDocuments.identityDocumentType"
            })
          }
          if (identityDocumentType?.value === "CN") {
            if (["BOLZANO", "AOSTA", "TRIESTE"].includes(documentPlaceOfIssue?.value?.toUpperCase())) {
              if (
                documentDateOfIssue.isAfter(moment("01/01/2017", "DD/MM/YYYY")) &&
                !value?.match(cnConditionDateOfIssue)
              ) {
                return this.createError({
                  message: identityNumberInvalid,
                  path: "identityDocuments.identityDocumentsNumber"
                })
              } else if (
                documentDateOfIssue.isBefore(moment("01/01/2017", "DD/MM/YYYY")) &&
                !value?.match(cnNumberWithConditionContriesRegex)
              ) {
                return this.createError({
                  message: identityNumberInvalid,
                  path: "identityDocuments.identityDocumentsNumber"
                })
              }
            } else if (!value?.match(cnNumberRegex)) {
              return this.createError({
                message: identityNumberInvalid,
                path: "identityDocuments.identityDocumentsNumber"
              })
            }
          }
          if (identityDocumentType?.value === "CE") {
            if (!value?.match(ceNumberRegex)) {
              return this.createError({
                message: identityNumberInvalid,
                path: "identityDocuments.identityDocumentsNumber"
              })
            }
          }
          if (identityDocumentType?.value === "PA") {
            if (!value?.match(paNumberRegex)) {
              return this.createError({
                message: identityNumberInvalid,
                path: "identityDocuments.identityDocumentsNumber"
              })
            }
          }
          if (identityDocumentType?.value === "PT") {
            if (
              !value
                .toUpperCase()
                ?.match(
                  "^" +
                    this.options.context.identityDocuments.documentProvinceOfIssue +
                    "([0-9]{6,7}[0-9a-zA-Z\\d]{0,2})$"
                )
            ) {
              return this.createError({
                message: identityNumberInvalid,
                path: "identityDocuments.identityDocumentsNumber"
              })
            }
          }
          if (identityDocumentType?.value === "PE") {
            const isPlaceIssueMatchingIdNumber =
              documentPlaceOfIssue?.value?.slice(0, 2) === identityDocumentsNumber?.slice(0, 2)

            const startsWithRegexLetterU = new RegExp(/^[uU]/)
            const isIdNumberBeginningWithU = startsWithRegexLetterU.test(identityDocumentsNumber)

            if (!isIdNumberBeginningWithU && !isPlaceIssueMatchingIdNumber) {
              return this.createError({
                message: identityNumberInvalid,
                path: "identityDocuments.identityDocumentsNumber"
              })
            }
            if (!value?.match(peRegexDefault)) {
              return this.createError({
                message: identityNumberInvalid,
                path: "identityDocuments.identityDocumentsNumber"
              })
            }
          }
        }
      }),
    documentPlaceOfIssue: yup
      .object()
      .nullable()
      .test({
        name: "documentPlaceOfIssue verification",
        message: "",
        test(documentPlaceOfIssue) {
          if (!documentPlaceOfIssue?.value) {
            return this.createError({
              message: requiredMessage,
              path: "identityDocuments.documentPlaceOfIssue"
            })
          }
        }
      }),
    documentDateOfIssue: yup
      .string()
      .nullable()
      .test({
        name: "documentDateOfIssue verification",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "identityDocuments.documentDateOfIssue"
            })
          } else {
            const documentDateOfIssue = moment(
              this.options.context.identityDocuments.documentDateOfIssue,
              DATE_FORMAT.identityDocuments.documentDateOfIssue.IT,
              true
            )
            const identityDocumentType = this.options.context.identityDocuments.identityDocumentType
            const birthDate = moment(
              this.options.context.identity.birthDate,
              DATE_FORMAT.identityDocuments.documentDateOfIssue.IT
            )
            const maximumDate = moment("04/02/2012", "DD/MM/YYYY")
            const toDay = moment()

            if (
              !documentDateOfIssue.isValid() ||
              documentDateOfIssue.year() < 1000 ||
              documentDateOfIssue.year() > 9999
            ) {
              return this.createError({
                message: documentDateOfIssueInvalid,
                path: "identityDocuments.documentDateOfIssue"
              })
            }

            if (!birthDate.isValid() || birthDate.year() < 1000 || birthDate.year() > 9999) {
              return this.createError({
                message: documentDateOfIssueInvalid,
                path: "identityDocuments.documentDateOfIssue"
              })
            }

            if (!identityDocumentType?.value) {
              return this.createError({
                message: documentNotValidMessage,
                path: "identityDocuments.documentDateOfIssue"
              })
            }

            if (documentDateOfIssue.isAfter(toDay)) {
              return this.createError({
                message: documentNotValidMessage,
                path: "identityDocuments.documentDateOfIssue"
              })
            }

            if (identityDocumentType?.value === "CE" || identityDocumentType?.value === "CN") {
              if (documentDateOfIssue > maximumDate) {
                let documentValidityDate = documentDateOfIssue.add(10, "year")
                if (toDay > documentValidityDate) {
                  let nextBirthday = birthDate.set("year", documentValidityDate.year()).isBefore(documentValidityDate)
                    ? birthDate.set("year", documentValidityDate.year() + 1)
                    : birthDate.set("year", documentValidityDate.year())
                  if (toDay < nextBirthday) {
                    return
                  }
                  return this.createError({
                    message: documentNotValidMessage,
                    path: "identityDocuments.documentDateOfIssue"
                  })
                }
              } else {
                return this.createError({
                  message: documentNotValidMessage,
                  path: "identityDocuments.documentDateOfIssue"
                })
              }
            }

            if (identityDocumentType?.value === "PA") {
              let documentValidityDate = documentDateOfIssue.add(10, "year")
              if (toDay > documentValidityDate) {
                return this.createError({
                  message: documentNotValidMessage,
                  path: "identityDocuments.documentDateOfIssue"
                })
              }
            }

            if (
              identityDocumentType?.value === "PT" ||
              (identityDocumentType?.value === "PE" &&
                documentDateOfIssue.isBefore(
                  moment("17/09/2012", DATE_FORMAT.identityDocuments.documentDateOfIssue.IT)
                ))
            ) {
              let documentValidityDate = birthDate.add(16, "year")
              if (documentDateOfIssue < documentValidityDate) {
                return this.createError({
                  message: documentNotValidMessage,
                  path: "identityDocuments.documentDateOfIssue"
                })
              }
            }

            if (
              identityDocumentType?.value === "PE" &&
              documentDateOfIssue.isAfter(moment("17/09/2012", DATE_FORMAT.identityDocuments.documentDateOfIssue.IT))
            ) {
              let documentValidityDate = documentDateOfIssue.add(10, "year")
              let nextBirthday = birthDate.set("year", documentValidityDate.year()).isBefore(documentValidityDate)
                ? birthDate.set("year", documentValidityDate.year() + 1)
                : birthDate.set("year", documentValidityDate.year())
              if (toDay > nextBirthday) {
                return this.createError({
                  message: documentNotValidMessage,
                  path: "identityDocuments.documentDateOfIssue"
                })
              }
            }
          }
        }
      }),
    identityDocumentsDriverLicenseNumber: yup
      .string()
      .nullable()
      .test({
        name: "identityDocumentsDriverLicenseNumber verification",
        message: "",
        test(value) {
          let identityDocumentType = this.options.context.identityDocuments.identityDocumentType?.value
          const issuePlace = this.options.context.identityDocuments.documentPlaceOfIssue?.label
          if (IDENTITY_DOCUMENT_TYPE_PATENTE.includes(identityDocumentType)) {
            if (!value) {
              return this.createError({
                message: requiredMessage,
                path: "identityDocuments.identityDocumentsDriverLicenseNumber"
              })
            }

            if (!issuePlace) {
              return this.createError({
                message: requiredMessage,
                path: "identityDocuments.documentPlaceOfIssue"
              })
            }

            if (identityDocumentType === "PT" && !value.toUpperCase()?.match(retroPatenteRegex)) {
              return this.createError({
                message: patenteElectroniquaInvalid,
                path: "identityDocuments.identityDocumentsDriverLicenseNumber"
              })
            }
            if (identityDocumentType === "PE") {
              if (
                issuePlace !== null &&
                issuePlace.toUpperCase() === "BOLZANO" &&
                !value.toUpperCase()?.match(retroPatenteElectronicaBolzanoRegex) &&
                !value.toUpperCase()?.match(retroPatenteElectronicaBolzanoSecondRegex)
              ) {
                return this.createError({
                  message: patenteElectroniquaInvalid,
                  path: "identityDocuments.identityDocumentsDriverLicenseNumber"
                })
              } else if (
                (issuePlace === null || issuePlace.toUpperCase() !== "BOLZANO") &&
                !value.toUpperCase()?.match(retroPatenteElectronicaRegex)
              ) {
                return this.createError({
                  message: patenteElectroniquaInvalid,
                  path: "identityDocuments.identityDocumentsDriverLicenseNumber"
                })
              }
            }
          }
        }
      }),
    residencePermitType: yup
      .string()
      .nullable()
      .test({
        name: "residencePermitType verification",
        message: "",
        test(value) {
          let citizenshipSimplified = this.options.context.identityDocuments.citizenshipSimplified
          if (citizenshipSimplified?.value === "3" && !value) {
            return this.createError({
              message: requiredMessage,
              path: "identityDocuments.residencePermitType"
            })
          }
        }
      }),
    temporaryDocument: yup
      .string()
      .nullable()
      .test({
        name: "temporaryDocument verification",
        message: "",
        test(value) {
          let citizenshipSimplified = this.options.context.identityDocuments.citizenshipSimplified?.value

          if (citizenshipSimplified === "3" && !value) {
            return this.createError({
              message: requiredMessage,
              path: "identityDocuments.temporaryDocument"
            })
          }
        }
      }),
    residencePermitExpiryDate: yup
      .string()
      .nullable()
      .test({
        name: "residencePermitExpiryDate verification",
        message: "",
        test(value) {
          let citizenshipSimplified = this.options.context.identityDocuments.citizenshipSimplified
          let temporaryDocument = this.options.context.identityDocuments.temporaryDocument
          let residencePermitExpiryDate = moment(value, DATE_FORMAT.identityDocuments.residencePermitExpiryDate.IT)
          let currentDate = moment()

          if (temporaryDocument === "RR" && citizenshipSimplified?.value === "3") {
            if (!value) {
              return this.createError({
                message: requiredMessage,
                path: "identityDocuments.residencePermitExpiryDate"
              })
            }
            if (
              !residencePermitExpiryDate.isValid() ||
              residencePermitExpiryDate.year() < 1000 ||
              residencePermitExpiryDate.year() > 9999
            ) {
              return this.createError({
                message: invalidDateResidencepermit,
                path: "identityDocuments.residencePermitExpiryDate"
              })
            }
            if (!currentDate.isBefore(residencePermitExpiryDate)) {
              return this.createError({
                message: invalidDateResidencepermit,
                path: "identityDocuments.residencePermitExpiryDate"
              })
            }
          }
        }
      })
  }),
  situation: yup.object().shape({
    profession: yup.string().nullable().required(requiredMessage),
    employmentDate: yup
      .string()
      .nullable()
      .test({
        name: "employmentDate verification",
        message: "",
        test(value) {
          let profession = this.options.context.situation.profession?.value
          if (!DATA_INIZIO_LAVARO_NO_VALIDATE_PROFESSIONS.includes(profession)) {
            if (!value) {
              return this.createError({
                message: requiredMessage,
                path: "situation.employmentDate"
              })
            }
            if (!value?.match(employmentDateRegex)) {
              return this.createError({
                message: employmentDateIvalidMessage,
                path: "situation.employmentDate"
              })
            }
            const employmentDate = moment(value, DATE_FORMAT.situation.employmentDate.IT, true)
            if (!employmentDate.isValid() || employmentDate.year() < 1000 || employmentDate.year() > 9999) {
              return this.createError({
                message: dateYYYYFormatValidationMessage,
                path: "situation.employmentDate"
              })
            }
            if (employmentDate.isAfter(moment())) {
              return this.createError({
                message: futureEmploymentDateMessage,
                path: "situation.employmentDate"
              })
            }
            const birthDate = moment(this.options.context.identity.birthDate, DATE_FORMAT.identity.birthDate.FR, true)
            if (!this.options.context.identity.birthDate) {
              return this.createError({
                message: invalidBirthDateMessage,
                path: "situation.employmentDate"
              })
            }
            if (!birthDate.isValid()) {
              return this.createError({
                message: invalidBirthDateMessage,
                path: "situation.employmentDate"
              })
            }
            if (birthDate.isAfter(employmentDate)) {
              return this.createError({
                message: employmentDateBeforeBirthDateMessage,
                path: "situation.employmentDate"
              })
            }
            const diff = employmentDate.year() - birthDate.year()
            if (diff < 16) {
              return this.createError({
                message: wrongDiffMessage,
                path: "situation.employmentDate"
              })
            }
          }
        }
      }),
    companyName: yup
      .string()
      .nullable()
      .max(50, maximumCharactersMessage(50))
      .test({
        name: "companyName verification",
        message: "",
        test(value) {
          let professsion = this.options.context.situation.profession?.value
          if (this.options.context.situation.hideCompanyInfo) {
            if (!value && !DATORE_CITTA_PROVINCIA_NO_VALIDATE_PROFESSIONS.includes(professsion)) {
              return this.createError({
                message: requiredMessage,
                path: "situation.companyName"
              })
            }
          }
          if (
            !this.options.context.situation.hideCompanyInfo &&
            !value &&
            !DATORE_CITTA_PROVINCIA_NO_VALIDATE_PROFESSIONS.includes(professsion) &&
            professsion
          ) {
            return this.createError({
              message: requiredMessage,
              path: "situation.companyName"
            })
          }
        }
      }),
    companyCity: yup
      .string()
      .nullable()
      .test({
        name: "companyCity verification",
        message: "",
        test(value) {
          let professsion = this.options.context.situation.profession?.value
          if (!value && !DATORE_CITTA_PROVINCIA_NO_VALIDATE_PROFESSIONS.includes(professsion)) {
            return this.createError({
              message: requiredMessage,
              path: "situation.companyCity"
            })
          }
        }
      }),
    companyProvince: yup
      .string()
      .nullable()
      .test({
        name: "companyProvince verification",
        message: "",
        test(value) {
          let professsion = this.options.context.situation.profession?.value
          if (!value && !DATORE_CITTA_PROVINCIA_NO_VALIDATE_PROFESSIONS.includes(professsion)) {
            return this.createError({
              message: requiredMessage,
              path: "situation.companyProvince"
            })
          }
        }
      }),
    residenceDate: yup
      .string()
      .nullable()
      .test({
        name: "residenceDate verification",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "situation.residenceDate"
            })
          }
          const residenceDate = moment(
            value,
            DATE_FORMAT.situation.residenceDate[this.options.context.countryLanguage],
            true
          )
          if (
            !residenceDate.isValid() ||
            (residenceDate.isValid() && (residenceDate.year() < 1000 || residenceDate.year() > 9999))
          ) {
            return this.createError({
              message: dateYYYYFormatValidationMessage,
              path: "situation.residenceDate"
            })
          }
          if (residenceDate.isAfter(moment())) {
            return this.createError({
              message: futureResidenceDateMessage,
              path: "situation.residenceDate"
            })
          }
          const birthDate = moment(
            this.options.context.identity.birthDate,
            DATE_FORMAT.identity.birthDate[this.options.context.countryLanguage],
            true
          )
          if (!this.options.context.identity.birthDate || !birthDate.isValid()) {
            return this.createError({
              message: invalidBirthDateMessage,
              path: "situation.residenceDate"
            })
          }
          if (birthDate.isAfter(residenceDate)) {
            return this.createError({
              message: residenceDateBeforeBirthDateMessage,
              path: "situation.residenceDate"
            })
          }
        }
      }),
    yearBankSeniority: yup
      .string()
      .nullable()
      .test({
        name: "yearbankseniority verification",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "situation.yearBankSeniority"
            })
          }
          const yearbankseniority = moment(value)
          if (!yearbankseniority.isValid() || yearbankseniority.year() < 1000 || yearbankseniority.year() > 9999) {
            return this.createError({
              message: dateYYYYFormatValidationMessage,
              path: "situation.yearBankSeniority"
            })
          }
          // date must not be in future
          if (yearbankseniority.isAfter(moment())) {
            return this.createError({
              message: futureResidenceDateMessage,
              path: "situation.yearBankSeniority"
            })
          }

          // birthDate must be valid
          const birthDate = moment(this.options.context.identity.birthDate)
          if (!this.options.context.identity.birthDate) {
            return this.createError({
              message: invalidBirthDateMessage,
              path: "situation.yearBankSeniority"
            })
          }

          //birthDate must be before bank seniority Date
          if (birthDate.isAfter(yearbankseniority)) {
            return this.createError({
              message: yearSeniorityBeforeBirthDateMessage,
              path: "situation.yearBankSeniority"
            })
          }
        }
      }),
    netMonthlyIncome: yup
      .string()
      .max(9, maximumCharactersMessage(9))
      .nullable()
      .test({
        name: "netMonthlyIncome verification",
        message: "",
        test(value) {
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "situation.netMonthlyIncome"
            })
          }

          let professsion = this.options.context.situation.profession?.value
          if (!REDITTO_MENSILE_IN_EURO_NO_VALIDATE_PROFESSIONS.includes(professsion)) {
            if (!value) {
              return this.createError({
                message: requiredMessage,
                path: "situation.netMonthlyIncome"
              })
            }
          }
          if ((!value || value != null) && String(value).length > 9) {
            return this.createError({
              message: maximumCharactersMessage(9),
              path: "situation.netMonthlyIncome"
            })
          }
          if (value.startsWith("0")) {
            return this.createError({
              message: invalidNetMonthlyIncomeFormat,
              path: "situation.netMonthlyIncome"
            })
          }

          if (!String(value)?.match(netMonthlyIncomeRegex)) {
            return this.createError({
              message: invalidNetMonthlyIncomeFormat,
              path: "situation.netMonthlyIncome"
            })
          }
        }
      }),
    vatBusinessCode: yup
      .string()
      .nullable()
      .test({
        name: "vatNumberRegex verification",
        message: "",
        test(value) {
          if (!value && this.options.context.identity.isSmallBusiness) {
            return this.createError({
              message: requiredMessage,
              path: "situation.vatBusinessCode"
            })
          }
          if (!!value && !value?.toString().toUpperCase()?.match(vatNumberRegex)) {
            return this.createError({
              message: invalidVatNumber,
              path: "situation.vatBusinessCode"
            })
          }
        }
      }),
    workingSituation: yup.string().nullable().required(requiredMessage),
    endContractDate: yup
      .string()
      .nullable()
      .test({
        name: "end contract date validation",
        message: "",
        test(value) {
          const endContractDate = moment(value, "MM/YYYY")
          let workingSituation = this.options.context.situation.workingSituation?.value

          if (workingSituation === contractTypeEnum && !value) {
            return this.createError({
              message: requiredMessage,
              path: "situation.endContractDate"
            })
          }
          if (endContractDate.isBefore(moment())) {
            return this.createError({
              message: pastEndContractDate,
              path: "situation.endContractDate"
            })
          }
        }
      })
  })
})

const getTitleFromEnum = (numero) => {
  if (numero === "1") {
    return "M"
  }
  return "F"
}
export default (values) => {
  return schema.concat(genericSchema).validate(values, { abortEarly: false, context: values })
}
